import { useQuery } from '@apollo/client';
import { createBrowserHistory } from 'history';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { LoadingIndicatorForApp, StatusPage } from 'components';
import {
  DOCUMENT_STATES,
  MOST_RECENT_BOOK_LOCATION_MUTATION_INTERVAL_MILLISECONDS,
} from 'constants';
import {
  AnalyticsModifiersContext,
  BookValuesContext,
  BookLocationDocumentValuesContext,
  BookLocationModifiersContext,
  BookLocationInitialValuesContext,
  BookLocationValuesContext,
  DocumentValuesContext,
  NetworkStatusValuesContext,
} from 'contexts';
import { DocumentScrollEvent } from '../../events';
import { useDebounce, useUpdateMostRecentBookLocationMutation } from 'hooks';
import { BookDocumentInformation, BookLocation } from 'types';
import {
  documentUtilities,
  locationLogic,
  getLocalSettingsProxy,
  getCfiForWordOffset,
} from 'utilities';
import { getMostRecentBookLocation } from 'utilities/mostRecentBookLocationUtilities';
import MOST_RECENT_BOOK_LOCATION_FOR_SPECIFIC_BOOK from 'graphql/queries/MostRecentBookLocationForSpecificBookQuery.graphql';

const updateLocalSettings = (bookLocation) => {
  const localSettings = getLocalSettingsProxy(bookLocation.book.pk);

  localSettings.mostRecentBook.pk = bookLocation.book.pk;
  localSettings.mostRecentBook.timestamp = +new Date();
  localSettings.mostRecentBookLocation.documentIndex =
    bookLocation.document.index;
  localSettings.mostRecentBookLocation.cfi = bookLocation.cfi;
  localSettings.mostRecentBookLocation.timestamp = +new Date();
};

export const BookLocationProvider = ({ children }) => {
  const analytics = useContext(AnalyticsModifiersContext);
  const { book, getDocumentInformation } = useContext(BookValuesContext);
  const {
    cfi: documentCfi,
    frame,
    frameDocument,
    percentageScrolled: documentPercentageScrolled,
    state: documentState,
  } = useContext(DocumentValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);

  const bookRef = useRef(book);

  // Set the title
  document.title = `${book.title}`;

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const history = createBrowserHistory();
  const searchParams = useMemo(
    () =>
      new URLSearchParams(locationLogic.getSearchStringFromLocation(location)),
    [location],
  );

  const {
    loading: bookLocationLoading,
    error: bookLocationError,
    data: {
      epubLocationHistoryLatestByCurrentUserForEpub: bookLocationData,
    } = {},
  } = useQuery(MOST_RECENT_BOOK_LOCATION_FOR_SPECIFIC_BOOK, {
    variables: { epubId: book.pk },
    skip: Boolean(params.documentNumber) || isOffline, // skip history check if url already includes a document number
  });

  const [currentBookLocation, setCurrentBookLocation] = useState();
  const [initialBookLocation, setInitialBookLocation] =
    useState(currentBookLocation);
  const [goToLocationCalledAt, setGoToLocationCalledAt] = useState();
  const [rawSpotlightData, setRawSpotlightData] = useState(false);

  // Manage debouncing updates to the app url path
  const [path, setPath] = useState();
  const { value: pathDebounced } = useDebounce(path);
  const pathHistoryEntryShouldPush = useRef(false);

  // Track refs for your current location and for the location that came before it.
  // The current location is useful for things that need to immediately respond to
  // location changes, such as URL updating, without waiting for / depending on state.
  const currentBookLocationRef = useRef(currentBookLocation);
  const priorBookLocationRef = useRef(null);

  // Track state for document to jump to when using next/previous navigation.
  const [nextDocumentLocation, setNextDocumentLocation] = useState(null);
  const [previousDocumentLocation, setPreviousDocumentLocation] =
    useState(null);
  const [nextDocumentLocationWithText, setNextDocumentLocationWithText] =
    useState(null);
  const [isEndOfDocument, setIsEndOfDocument] = useState(false);

  const [shouldFocusFrame, setShouldFocusFrame] = useState(false);

  const [finishedDocumentIndex, setFinishedDocumentIndex] = useState(null);
  const finishedDocumentIndexRef = useRef(null);
  const newBookDocumentRef = useRef(null);

  const frameDocumentRef = useRef(frameDocument);

  // Save promises for every attempted goToLocation
  const goToLocationPromiseResolversRef = useRef([]);

  const setCurrentBookLocationAndTrackPrevious = useCallback(
    (newBookLocation) => {
      if (!initialBookLocation && newBookLocation) {
        setInitialBookLocation(newBookLocation);
      }

      priorBookLocationRef.current = currentBookLocationRef.current;
      currentBookLocationRef.current = newBookLocation;

      setCurrentBookLocation(newBookLocation);
    },
    [initialBookLocation],
  );

  // Update the app url path while taking a debounce into account.
  useEffect(
    function updateAppUrlDebounced() {
      if (pathDebounced) {
        const replace = !pathHistoryEntryShouldPush.current;

        navigate(pathDebounced, {
          replace,
        });

        pathHistoryEntryShouldPush.current = false;
      }
    },
    [navigate, pathDebounced],
  );

  /**
   * Asynchronously track the current frame document.
   *
   * This allows calling the shineSpotlight function in a context where the stateful value of frameDocument may be outdated,
   * such as within goToLocation after waiting for navigation to resolve.
   */
  useEffect(
    function updateFrameDocumentRef() {
      frameDocumentRef.current = frameDocument;
    },
    [frameDocument],
  );

  /**
   * Set the initial current book location.
   */
  useEffect(
    function setInitialCurrentBookLocation() {
      (async () => {
        if (bookLocationLoading || currentBookLocation) {
          return;
        }

        // If available, use the URL parameter values to set the initial location.
        if (params.documentNumber) {
          const newLocation = locationLogic.getLocationForPath(
            book,
            `${location.pathname}${location.hash}`,
          );

          if (newLocation.wordOffset) {
            const bookDocumentInformation = await getDocumentInformation(
              newLocation.document.index,
              null,
              BookDocumentInformation.MODES.NO_RANGE_RECTANGLES,
            );
            const cfi = getCfiForWordOffset(
              bookDocumentInformation,
              newLocation.wordOffset,
            );
            newLocation.cfi = cfi;
          }

          return setCurrentBookLocationAndTrackPrevious(newLocation);
        }

        // Otherwise, if we are on a URL like "/read/1" with no document, use the location from the user's history.
        setCurrentBookLocationAndTrackPrevious(
          getMostRecentBookLocation(book, bookLocationData),
        );
      })();
    },
    [
      book,
      bookLocationData,
      bookLocationLoading,
      currentBookLocation,
      getDocumentInformation,
      location,
      params,
      setCurrentBookLocationAndTrackPrevious,
    ],
  );

  useEffect(
    function swapBookForCurrentLocation() {
      if (
        book !== currentBookLocationRef?.current?.document?.book &&
        !bookLocationLoading &&
        currentBookLocation
      ) {
        bookRef.current = book;

        const newBookLocation = currentBookLocation.swapBook(book);
        currentBookLocationRef.current = newBookLocation;

        setCurrentBookLocation(newBookLocation);
      }
    },
    [book, bookLocationLoading, currentBookLocation],
  );

  const [updateMostRecentBookLocation] =
    useUpdateMostRecentBookLocationMutation();

  const { value: debouncedCurrentBookLocation } = useDebounce(
    currentBookLocation,
    MOST_RECENT_BOOK_LOCATION_MUTATION_INTERVAL_MILLISECONDS,
  );

  useEffect(
    function updateMostRecentBookLocationRequest() {
      if (!debouncedCurrentBookLocation || isOffline) {
        return;
      }

      updateMostRecentBookLocation({
        variables: {
          input: {
            epubId: book.pk,
            documentIndex: debouncedCurrentBookLocation.document.index,
            cfi: debouncedCurrentBookLocation.cfi,
          },
        },
      });
    },
    [
      book,
      debouncedCurrentBookLocation,
      isOffline,
      updateMostRecentBookLocation,
    ],
  );

  /**
   * goToLocation is a wrapper around setCurrentBookLocationAndTrackPrevious that returns a promise indicating that the location's document has been loaded.
   *
   * @method goToLocation
   * @param {Location} newLocation - the new location
   * @param {Boolean} replace - whether to replace the current browser history entry or add a new one (default: add new)
   * @returns {Promise<BookLocation>} a promise that will resolve to the next location to that fully loads.
   */
  const goToLocation = useCallback(
    async (
      newLocation,
      options = {
        shouldReplaceHistoryEntry: false,
      },
    ) => {
      newBookDocumentRef.current = newLocation;

      setGoToLocationCalledAt(new Date().getTime());
      const goToLocationPromise = new Promise((resolve) => {
        goToLocationPromiseResolversRef.current.push(resolve);
      });

      setCurrentBookLocationAndTrackPrevious(newLocation);

      pathHistoryEntryShouldPush.current =
        pathHistoryEntryShouldPush.current ||
        !options.shouldReplaceHistoryEntry;
      setPath(locationLogic.generatePath(newLocation, searchParams));

      return goToLocationPromise;
    },
    [setCurrentBookLocationAndTrackPrevious, searchParams],
  );

  const setCurrentBookLocationUsingURL = useCallback(
    (location) => {
      const bookLocation = locationLogic.getLocationForPath(
        bookRef.current,
        `${location.pathname}${location.hash}`,
      );

      setCurrentBookLocationAndTrackPrevious(bookLocation);
    },
    [setCurrentBookLocationAndTrackPrevious],
  );

  useEffect(
    function setCurrentBookLocationWithUrl() {
      // history.listen returns a function that stops listening when called,
      // so returning the call doubles as a cleanup method.
      return history.listen(({ location, action }) => {
        if (action === 'POP') {
          setCurrentBookLocationUsingURL(location);
        }
      });
    },
    [history, setCurrentBookLocationUsingURL],
  );

  const setPreviousAndNextDocumentLocations = useCallback(
    (frame, newLocation) => {
      const previousDoc = newLocation.document?.previous;
      const nextDoc = newLocation.document?.next;

      if (
        frame &&
        (previousDoc?.index !== previousDocumentLocation?.document?.index ||
          nextDoc?.index !== nextDocumentLocation?.document?.index)
      ) {
        setPreviousDocumentLocation(
          previousDoc
            ? new BookLocation({
                book,
                document: previousDoc,
                percentageOfDocument: 1,
              })
            : null,
        );
        setNextDocumentLocation(
          nextDoc
            ? new BookLocation({
                book,
                document: nextDoc,
                percentageOfDocument: 0,
              })
            : null,
        );
      }
    },
    [
      book,
      previousDocumentLocation?.document?.index,
      nextDocumentLocation?.document?.index,
    ],
  );

  useEffect(
    function updateNextDocumentLocationWithText() {
      let bookLocation = currentBookLocation?.document.next?.bookLocation;

      if (!bookLocation) {
        // If there is no current or next document, set to null.
        setNextDocumentLocationWithText(null);
        return;
      }

      // Find the next document that has text to read.
      while (!Boolean(bookLocation.document.characterCount)) {
        // As long as the selected document does not have a character count, move to the next document.
        bookLocation = bookLocation.document.next?.bookLocation;

        if (!bookLocation) {
          // If there are no more documents, set the nextDocumentLocationWithText to null.
          setNextDocumentLocationWithText(null);
          return;
        }
      }

      setNextDocumentLocationWithText(bookLocation);
    },
    [currentBookLocation],
  );

  useEffect(
    function determineIsEndOfDocument() {
      if (
        frame &&
        finishedDocumentIndex === currentBookLocation?.document?.index
      ) {
        // see if we're at the end of the document
        const scrollPercentage = documentUtilities.getPercentageScrolled(frame);
        const activeContainer = frame.parentElement;
        const isEndOfDocument = documentUtilities.getIsEndOfDocument(
          activeContainer,
          scrollPercentage,
        );
        setIsEndOfDocument(isEndOfDocument);
      }
    },
    [
      documentPercentageScrolled,
      frame,
      finishedDocumentIndex,
      currentBookLocation?.document?.index,
    ],
  );

  useEffect(
    function setCurrentBookLocation() {
      if (!currentBookLocationRef.current) {
        return;
      }

      const isFixedWidth = currentBookLocationRef.current.document.isFixedWidth;

      // Ensure the document has finished loading
      if (
        finishedDocumentIndexRef.current !==
        currentBookLocationRef.current.document.index
      ) {
        return;
      }

      // Ensure the document has been scrolled enough to change the CFI
      // only if not fixedWidth books, as same cfi is used for the whole document in those so location needs to change per scroll
      if (documentCfi === currentBookLocationRef.current.cfi && !isFixedWidth) {
        return;
      }

      // Create a new location.
      const newLocation = isFixedWidth
        ? new BookLocation({
            document: currentBookLocationRef.current.document,
            percentageOfDocument: documentPercentageScrolled,
            event: new DocumentScrollEvent(),
          })
        : new BookLocation({
            document: currentBookLocationRef.current.document,
            cfi: documentCfi,
            event: new DocumentScrollEvent(),
          });

      // Go to the location.
      setCurrentBookLocationAndTrackPrevious(newLocation);
    },
    [
      documentCfi,
      documentPercentageScrolled,
      setCurrentBookLocationAndTrackPrevious,
    ],
  );

  useEffect(
    function determineFinishedDocumentIndex() {
      if (!currentBookLocationRef.current) {
        return;
      }

      const isFinished = documentState >= DOCUMENT_STATES.FINISHED;

      if (isFinished) {
        finishedDocumentIndexRef.current =
          currentBookLocationRef.current.document.index;
        setFinishedDocumentIndex(currentBookLocationRef.current.document.index);

        return;
      }

      finishedDocumentIndexRef.current = null;
      setFinishedDocumentIndex(null);
    },
    [documentState],
  );

  // Whenever the currentBookLocation changes, or the document finishes loading,
  // Do actions that depend on the frame representing the same document as currentBookLocation.
  useEffect(
    function afterCurrentBookLocationChange() {
      if (!currentBookLocation) {
        return;
      }

      if (finishedDocumentIndex !== currentBookLocation.document.index) {
        return;
      }

      if (
        newBookDocumentRef.current &&
        newBookDocumentRef.current.document.index !==
          currentBookLocation.document.index
      ) {
        return;
      }

      newBookDocumentRef.current = null;

      // Resolve associations
      currentBookLocation.resolve(frame);

      // set the nextDocumentLocation and previousDocumentLocation based on the current frame
      setPreviousAndNextDocumentLocations(frame, currentBookLocation);
      // Update location in the book.
      updateLocalSettings(currentBookLocation);

      // Publish a page-turn event if applicable.
      if (
        currentBookLocation.page?.index !==
        priorBookLocationRef.current?.page?.index
      ) {
        // this is amplitude
        analytics.pageViewed({
          pageNumber: currentBookLocation.page?.label,
          pageIndex: currentBookLocation.page?.index,
        });
      }

      // Resolve all goToLocation promises.
      goToLocationPromiseResolversRef.current.forEach((resolve) => {
        resolve(currentBookLocation);
      });
      goToLocationPromiseResolversRef.current = [];
    },
    [
      analytics,
      currentBookLocation,
      finishedDocumentIndex,
      frame,
      frameDocument,
      getDocumentInformation,
      goToLocationCalledAt, // ensure goToLocation promises resolve, even if the location is the same
      setPreviousAndNextDocumentLocations,
    ],
  );

  // Some things should happen whenever the currentBookLocation changes,
  // but do not require the frame or for the location to be resolved.
  useEffect(
    function updatePath() {
      if (!currentBookLocationRef.current) {
        return;
      }

      // Keep the URL hash updated with the most accurate positioning parameter available. Ideally CFI.
      setPath(
        locationLogic.generatePath(
          currentBookLocationRef.current,
          searchParams,
        ),
      );
    },
    [currentBookLocation, currentBookLocation?.resolved, searchParams],
  );

  window.v7.location = currentBookLocation;

  const initialValues = useMemo(
    () => ({ initialBookLocation }),
    [initialBookLocation],
  );

  const currentDocumentValues = useMemo(
    () => ({
      currentBookLocationDocument: currentBookLocation?.document,
      nextDocumentLocation,
      nextDocumentLocationWithText,
      previousDocumentLocation,
      isEndOfDocument,
    }),
    [
      currentBookLocation?.document,
      nextDocumentLocation,
      nextDocumentLocationWithText,
      previousDocumentLocation,
      isEndOfDocument,
    ],
  );

  const values = useMemo(
    () => ({
      currentBookLocation,
      goToLocationCalledAt,
      shouldFocusFrame,
      rawSpotlightData,
    }),
    [
      currentBookLocation,
      goToLocationCalledAt,
      shouldFocusFrame,
      rawSpotlightData,
    ],
  );

  const modifiers = useMemo(
    () => ({
      goToLocation,
      setShouldFocusFrame,
      setRawSpotlightData,
    }),
    [goToLocation, setShouldFocusFrame, setRawSpotlightData],
  );

  if (bookLocationError) {
    return (
      <StatusPage
        statusCode={bookLocationError.networkError?.statusCode}
        bookPk={currentBookLocation?.book?.pk}
      />
    );
  }

  return (
    <BookLocationInitialValuesContext.Provider value={initialValues}>
      <BookLocationDocumentValuesContext.Provider value={currentDocumentValues}>
        <BookLocationValuesContext.Provider value={values}>
          <BookLocationModifiersContext.Provider value={modifiers}>
            {currentBookLocation ? children : <LoadingIndicatorForApp />}
          </BookLocationModifiersContext.Provider>
        </BookLocationValuesContext.Provider>
      </BookLocationDocumentValuesContext.Provider>
    </BookLocationInitialValuesContext.Provider>
  );
};

export default BookLocationProvider;
